/**
 * Environment Configuration Service
 * 
 * Service functions for accessing environment configuration from Firebase.
 * All database operations are encapsulated here.
 */

import { getDb, auth } from '@/lib/firebase-config';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

export interface EnvDocument {
  GITHUB_TOKEN?: string;
  [key: string]: any;
}
import logger from '@/lib/logging';

/**
 * App-wide settings that administrators can configure
 * Stored in the 'app_settings' collection
 */
export interface AppSettings {
  /** @deprecated Use diagnosticLoggingEnabled */
  authDebugLoggingEnabled?: boolean;
  diagnosticLoggingEnabled?: boolean;
  diagnosticLogFlushIntervalMinutes?: number;
  clientLogLevel?: 'debug' | 'info' | 'warn' | 'error';
  serverLogLevel?: 'debug' | 'info' | 'warn' | 'error';
  saveSelfAssessmentPictures?: boolean;
  // Add more app-wide settings here as needed
}

const APP_SETTINGS_COLLECTION = 'app_settings';
const APP_SETTINGS_DOC_ID = 'global';

/** Defaults when Firestore read is denied (e.g. anonymous preview) or document is missing. */
const DEFAULT_APP_SETTINGS: AppSettings = {
  diagnosticLoggingEnabled: false,
  diagnosticLogFlushIntervalMinutes: 5,
  clientLogLevel: 'info',
  serverLogLevel: 'info',
  saveSelfAssessmentPictures: true,
};

/**
 * Get environment configuration document
 * @param documentId - The document ID in the env collection (default: 'kqMMGU0gw0VORN7AkKEY')
 */
export async function getEnvDocument(documentId: string = 'kqMMGU0gw0VORN7AkKEY'): Promise<EnvDocument | null> {
  try {
    const db = getDb();
    const envDocRef = doc(db, 'env', documentId);
    const envDoc = await getDoc(envDocRef);

    if (envDoc.exists()) {
      return envDoc.data() as EnvDocument;
    }

    return null;
  } catch (error) {
    logger.error('Error getting env document:', error);
    throw error;
  }
}

/**
 * Get GitHub token from environment configuration
 * 
 * @deprecated This function is primarily for backward compatibility.
 * The server-side flow now handles token resolution with fallback logic.
 * Client-side code should use the /api/protected/feedback/create-issue endpoint
 * instead of directly accessing the GitHub token.
 * 
 * @param documentId - The document ID (optional)
 * @returns The GitHub token or null
 */
export async function getGitHubToken(documentId: string = 'kqMMGU0gw0VORN7AkKEY'): Promise<string | null> {
  try {
    const envDoc = await getEnvDocument(documentId);
    return envDoc?.GITHUB_TOKEN || null;
  } catch (error) {
    logger.error('Error getting GitHub token:', error);
    return null;
  }
}

/**
 * Get app-wide settings
 */
export async function getAppSettings(): Promise<AppSettings | null> {
  try {
    // Invite-only Firestore rules: unauthenticated users cannot read app_settings.
    // PR preview / public landing uses anonymous browsing — avoid a permission-denied round trip.
    if (!auth?.currentUser) {
      return { ...DEFAULT_APP_SETTINGS };
    }

    const db = getDb();
    const settingsDocRef = doc(db, APP_SETTINGS_COLLECTION, APP_SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsDocRef);

    if (settingsDoc.exists()) {
      return settingsDoc.data() as AppSettings;
    }

    return { ...DEFAULT_APP_SETTINGS };
  } catch (error: any) {
    const code = error?.code ?? error?.message;
    if (code === 'permission-denied' || String(code).includes('permission')) {
      logger.debug('getAppSettings: permission denied, using defaults (signed-in user may lack app_settings read)');
      return { ...DEFAULT_APP_SETTINGS };
    }
    logger.error('Error getting app settings:', error);
    return null;
  }
}

/**
 * Update app-wide settings (admin only)
 */
export async function updateAppSettings(settings: Partial<AppSettings>): Promise<void> {
  try {
    const db = getDb();
    const settingsDocRef = doc(db, APP_SETTINGS_COLLECTION, APP_SETTINGS_DOC_ID);

    // Check if document exists
    const existingDoc = await getDoc(settingsDocRef);

    if (existingDoc.exists()) {
      await updateDoc(settingsDocRef, settings);
    } else {
      // Create the document with default values merged with provided settings
      await setDoc(settingsDocRef, {
        diagnosticLoggingEnabled: false,
        diagnosticLogFlushIntervalMinutes: 5,
        ...settings,
      });
    }

    logger.debug('✅ [AppSettings] Updated app settings:', settings);
  } catch (error) {
    logger.error('Error updating app settings:', error);
    throw error;
  }
}

/**
 * Check if auth debug logging is enabled (deprecated - use isDiagnosticLoggingEnabled)
 */
export async function isAuthDebugLoggingEnabled(): Promise<boolean> {
  return isDiagnosticLoggingEnabled();
}

/**
 * Check if diagnostic logging is enabled.
 * Backward compatible: reads diagnosticLoggingEnabled or authDebugLoggingEnabled.
 */
export async function isDiagnosticLoggingEnabled(): Promise<boolean> {
  try {
    const settings = await getAppSettings();
    return settings?.diagnosticLoggingEnabled ?? settings?.authDebugLoggingEnabled ?? false;
  } catch (error) {
    logger.error('Error checking diagnostic logging status:', error);
    return false;
  }
}

/**
 * Get the diagnostic log flush interval in minutes (default: 5)
 */
export async function getDiagnosticLogFlushIntervalMinutes(): Promise<number> {
  try {
    const settings = await getAppSettings();
    const val = settings?.diagnosticLogFlushIntervalMinutes;
    return typeof val === 'number' && val > 0 ? val : 5;
  } catch (error) {
    logger.error('Error getting diagnostic log flush interval:', error);
    return 5;
  }
}

/**
 * Get the current client log level
 */
export async function getClientLogLevel(): Promise<'debug' | 'info' | 'warn' | 'error'> {
  try {
    const settings = await getAppSettings();
    return settings?.clientLogLevel ?? 'info';
  } catch (error) {
    logger.error('Error getting client log level:', error);
    return 'info';
  }
}

/**
 * Get the current server log level
 */
export async function getServerLogLevel(): Promise<'debug' | 'info' | 'warn' | 'error'> {
  try {
    const settings = await getAppSettings();
    return settings?.serverLogLevel ?? 'info';
  } catch (error) {
    logger.error('Error getting server log level:', error);
    return 'info';
  }
}
